import { ApolloClient } from '@apollo/client'

import {
  GetSignedUnverifiedUploadUrlDocument,
  VerifyUploadedFileDocument,
} from '~/client/graph/operations/generated/FileUploader.generated'
import {
  IAttachment,
  SourceEntityType,
  UploadingType,
} from '~/client/graph/types.generated'
import {
  imageExtensions,
  msExcelExtensions,
  msPowerPointExtensions,
  msWordExtensions,
  pdfExtension,
} from '~/client/src/shared/stores/domain/FileUploading.store'

function getUploadingType(file: File): UploadingType {
  const extension = file.name?.split('.')?.pop()?.toLowerCase()
  if (msWordExtensions.includes(extension)) return UploadingType.MsWord
  if (msPowerPointExtensions.includes(extension))
    return UploadingType.MsPowerPoint
  if (msExcelExtensions.includes(extension)) return UploadingType.MsExcel
  if (extension === pdfExtension) return UploadingType.Pdf
  if (imageExtensions.includes(extension)) return UploadingType.Image
  return UploadingType.AttachmentFile
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, i)
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

export async function uploadPublicFile(
  apolloClient: ApolloClient<any>,
  file: File,
): Promise<IAttachment> {
  const uploadingType = getUploadingType(file)

  const { data: signedData } = await apolloClient.query({
    query: GetSignedUnverifiedUploadUrlDocument,
    variables: {
      fileName: file.name,
      uploadingType,
    },
    fetchPolicy: 'no-cache',
  })

  const requisites = signedData?.getSignedUnverifiedUploadUrl
  if (!requisites?.length || !requisites[0]) {
    throw new Error('Failed to get upload URL')
  }

  const requisite = requisites[0]
  if (!requisite.signedUrl || !requisite.contentType || !requisite.filePath) {
    throw new Error('Missing required upload requisites')
  }

  await fetch(requisite.signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': requisite.contentType },
    body: file,
  })

  const { data: verifyData } = await apolloClient.query({
    query: VerifyUploadedFileDocument,
    variables: {
      input: {
        fileName: requisite.filePath,
        uploadingType,
        isPublic: true,
        logUpload: false,
        sourceEntityType: SourceEntityType.Form,
        sourceEntityCode: '',
        sourceEntityTypeName: '',
        locationName: '',
      },
    },
    fetchPolicy: 'no-cache',
  })

  const verified = verifyData?.verifyUploadedFile
  if (!verified?.fileURL) {
    throw new Error('Failed to verify uploaded file')
  }

  return {
    fileName: file.name,
    size: formatFileSize(file.size),
    url: verified.fileURL,
  }
}
